import { useState, useCallback, useEffect } from 'react';


export interface DropdownPosition {
  direction: 'down' | 'up';
  top: number;
  left: number;
  width: number;
}

export interface UseSelectorPositionOptions {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  maxMenuHeight: number;
  searchable: boolean;
  portal: boolean;
  open: boolean;
  options: { length: number };
  search: string;
}

export interface UseSelectorPositionReturn {
  openDirection: 'down' | 'up';
  portalStyle: { top: number; left: number; width: number } | null;
  computeAndSetPosition: () => void;
}

/**
 * Hook to manage dropdown position calculation
 */
export function useSelectorPosition({
  triggerRef,
  maxMenuHeight,
  searchable,
  portal,
  open,
  options,
  search,
}: UseSelectorPositionOptions): UseSelectorPositionReturn {
  const [openDirection, setOpenDirection] = useState<'down' | 'up'>('down');
  const [portalStyle, setPortalStyle] = useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);

  const computePosition = useCallback((optionsCount: number): DropdownPosition | null => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    
    const OPTION_HEIGHT = 38;
    const SEARCH_BAR_HEIGHT = searchable ? 60 : 0;
    const MENU_PADDING = 16;
    const MIN_MENU_HEIGHT = OPTION_HEIGHT + MENU_PADDING + SEARCH_BAR_HEIGHT;
    
    const estimatedContentHeight = 
      (optionsCount * OPTION_HEIGHT) + 
      SEARCH_BAR_HEIGHT + 
      MENU_PADDING;
    
    const estimatedMenuHeight = Math.max(
      MIN_MENU_HEIGHT,
      Math.min(estimatedContentHeight, maxMenuHeight),
    );
    
    let direction: 'down' | 'up';
    if (spaceBelow >= estimatedMenuHeight) {
      direction = 'down';
    } else if (spaceAbove >= estimatedMenuHeight) {
      direction = 'up';
    } else {
      direction = spaceBelow >= spaceAbove ? 'down' : 'up';
    }
    
    return {
      direction,
      top: (direction === 'down' ? rect.bottom : rect.top) + window.scrollY,
      left: rect.left + window.scrollX,
      width: rect.width,
    };
  }, [maxMenuHeight, searchable, triggerRef]);

  const computeAndSetPosition = useCallback(() => {
    const computed = computePosition(options.length);
    if (!computed) return;
    setOpenDirection(computed.direction);
    if (portal) {
      setPortalStyle({ top: computed.top, left: computed.left, width: computed.width });
    }
  }, [computePosition, options.length, portal]);

  // Reposition on resize/scroll when open
  useEffect(() => {
    if (!open) return;
    
    const handleReposition = () => {
      computeAndSetPosition();
    };
    
    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, { passive: true });
    
    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition);
    };
  }, [open, computeAndSetPosition, search]);

  return {
    openDirection,
    portalStyle,
    computeAndSetPosition,
  };
}
